import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import { getBackendTokenSilently } from "./MsalAuthHandler";

interface ApiResult<T> {
    data: T | null;
    error?: string;
    status?: number;
}

export class CoreAPI {
    private baseURL = process.env.REACT_APP_API_URL;
    private axios: AxiosInstance;

    constructor() {
        this.axios = axios.create({
            baseURL: this.baseURL,
            headers: {
                "Content-Type": "application/json",
            },
        });

        this.axios.interceptors.request.use(
            async (config: InternalAxiosRequestConfig) => {
                const token = await getBackendTokenSilently();
                if (!config.headers) {
                    config.headers = {} as any;
                }
                (config.headers as any)["Authorization"] = `Bearer ${token}`;
                return config;
            },
            (error) => Promise.reject(error)
        );
    }

    async get<T = any>(endpoint: string): Promise<ApiResult<T>> {
        try {
            const response = await this.axios.get<T>(endpoint);

            return { data: response.data, status: response.status };
        } catch (err: any) {
            return { data: err.response.data, error: err.message, status: err.status };
        }
    }

    async getBlob(endpoint: string): Promise<ApiResult<Blob>> {
        try {
            const response = await this.axios.get(endpoint, {
                responseType: "blob"
            });

            return { data: response.data, status: response.status };
        } catch (err: any) {
            return { data: err.response.data, error: err.message, status: err.status };
        }
    }

    async post<T = any>(endpoint: string, data?: any): Promise<ApiResult<T>> {
        try {
            const response = await this.axios.post<T>(endpoint, data);
            return { data: response.data, status: response.status };
        } catch (err: any) {
            return { data: err.response.data, error: err.message, status: err.status };
        }
    }

    async patch<T = any>(endpoint: string, data?: any): Promise<ApiResult<T>> {
        try {
            const response = await this.axios.patch<T>(endpoint, data);
            return { data: response.data, status: response.status };
        } catch (err: any) {
            return { data: err.response.data, error: err.message, status: err.status };
        }
    }

    async delete<T = any>(endpoint: string, data?: any): Promise<ApiResult<T>> {
        try {
            const response = await this.axios.delete<T>(endpoint, { data: data });
            return { data: response.data, status: response.status };
        } catch (err: any) {
            return { data: err.response.data, error: err.message, status: err.status };
        }
    }
}

export const coreApi = new CoreAPI();